import PageShell from "@/components/site/PageShell";
import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { usePageContent } from "@/i18n/pageContent";

const BOARD = [
  { name: "Eurelia", role: "Official Guide & Digital Advisor", img: "/team-eurelia.jpg" },
  { name: "Riccardo D'Urso", role: "President", img: "/team-riccardo-durso.jpg" },
  { name: "Gregorio Bonavolontà", role: "Executive Director", img: "/team-gregorio-bonavolonta.jpg" },
  { name: "Daniela D'Urso", role: "Executive Director", img: "/team-daniela-durso.jpg" },
  { name: "Gaetano S.G. Cola", role: "Executive Director", img: "/team-gaetano-cola.jpg" },
];

const COUNCIL = [
  { name: "Carlo Dugo", role: "Diplomatic Advisor for International Affairs", img: "/art-dugo.png", url: "/articles/dugo-2026-03-10" },
  { name: "Enzo Casadio", role: "National Advisor for Territorial Development", img: "/art-casadio.jpg", url: "/articles/casadio-2026-03-18" },
  { name: "Fabiana D'Urso", role: "Scientific Advisor for Innovation & Advanced Technologies", img: "/art-fabiana-durso.png", url: "/articles/fabiana-durso-2026-03-27" },
  { name: "Arato Horimoto", role: "International Advisor for EU–Japan Relations", img: "/art-horimoto.png", url: "/articles/horimoto-2026-04-03" },
  { name: "Giuliana D'Antuono", role: "Strategic Advisor for International Promotion & Development", img: "/art-dantuono.png", url: "/articles/dantuono-2026-04-08" },
  { name: "Vincent Riotta", role: "Advisor for Audiovisual Productions", img: "/art-riotta.png", url: "/articles/riotta-2026-04-15" },
  { name: "Antonio Morra", role: "Legal Advisor", img: "/art-morra.png", url: "/articles/morra-2026-04-21" },
  { name: "Antonio Galoforo", role: "Scientific Advisor for Health, Innovation & Sustainability – One Health", img: "/art-galoforo.png", url: "/articles/galoforo-2026-05-05" },
  { name: "Stefania Bandini", role: "Diplomatic Advisor for Relations with Universities & Governmental Institutions", img: "/art-bandini.png", url: "/articles/bandini-2026-05-13" },
];

function MemberCard({ m }) {
  const inner = (
    <>
      <div className="relative overflow-hidden aspect-[4/5] w-full bg-[#fafaf7]">
        <img
          src={m.img}
          alt={m.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
        />
        {m.url && (
          <>
            {/* Navy gradient overlay */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0f2340]/85 via-[#0f2340]/25 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            {/* Gold "Read Article" badge */}
            <div className="absolute left-4 bottom-4 flex items-center gap-2 px-3 py-1.5 bg-[#d4af37] text-[#0f2340] font-body text-[10px] tracking-[0.28em] uppercase font-semibold translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 ease-out shadow-[0_6px_18px_rgba(15,35,64,0.35)]">
              Read Article <ArrowUpRight size={12} strokeWidth={2.5} />
            </div>
          </>
        )}
      </div>
      <div className="pt-5">
        <h3 className="font-display text-xl text-[#1a365d] group-hover:text-[#d4af37] transition-colors leading-snug">
          {m.name}
        </h3>
        <p className="font-body text-[12.5px] text-[#475569] mt-1.5 leading-relaxed">
          {m.role}
        </p>
      </div>
    </>
  );
  return m.url ? (
    <Link to={m.url} className="group card-lift block reveal cursor-pointer">
      {inner}
    </Link>
  ) : (
    <div className="group reveal">{inner}</div>
  );
}

export default function OurTeam() {
  const c = usePageContent("ourTeam");
  return (
    <PageShell>
      <PageHero
        eyebrow={c.eyebrow}
        title={<>{c.titleA} <span className="italic">{c.titleB}</span>.</>}
        subtitle={c.subtitle}
        image="/team-hero.jpg"
      />

      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-12 reveal">
            <div>
              <span className="eyebrow">{c.boardEyebrow}</span>
              <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-tight">
                {c.boardTitle}
              </h2>
            </div>
            <span className="hidden md:inline text-[11px] tracking-[0.32em] uppercase text-[#475569]">
              05 {c.boardMembers}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 md:gap-8">
            {BOARD.map((m) => (
              <MemberCard key={m.name} m={m} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#fafaf7] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between gap-6 mb-12 reveal">
            <div>
              <span className="eyebrow">{c.councilEyebrow}</span>
              <h2 className="font-display text-3xl md:text-5xl text-[#1a365d] mt-4 leading-tight">
                {c.councilTitle1} <span className="italic">{c.councilTitle2}</span>.
              </h2>
            </div>
            <span className="hidden md:inline text-[11px] tracking-[0.32em] uppercase text-[#475569]">
              {COUNCIL.length} {c.councilMembers}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
            {COUNCIL.map((m) => (
              <MemberCard key={m.name} m={m} />
            ))}
          </div>

          <div className="mt-16 md:mt-20 flex justify-center reveal">
            <Link
              to="/npt-apply"
              className="btn-outline"
            >
              {c.joinCta} <ArrowUpRight size={14} strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
